import { EnvironmentHelper } from '../../../libs/IdeLink.Web.Common/src/Helpers/EnvironmentHelper';
import { ListUtils } from '../../../libs/IdeLink.Web.Common/src/utils/ListUtils';
import { AreasPermissions } from '../../modules/Areas/Services/AreasPermissions';
import { AuditsPermissions } from '../../modules/Audits/Services/AuditsPermissions';
import { BackUsersPermissions } from '../../modules/BackUsers/Services/BackUsersPermissions';
import { ChallengesPermissions } from '../../modules/Challenges/Services/ChallengesPermissions';
import { CollectionsPermissions } from '../../modules/Collections/Services/CollectionsPermissions';
import { CommunicationsPermissions } from '../../modules/Communications/Services/CommunicationsPermissions';
import { ContactsPermissions } from '../../modules/Contacts/Services/ContactsPermissions';
import { EventsPermissions } from '../../modules/Events/Services/EventsPermissions';
import { FrontUsersPermissions } from '../../modules/FrontUsers/Services/FrontUsersPermissions';
import { GondolesPermissions } from '../../modules/Gondoles/Services/GondolesPermissions';
import { IndicatorsPermissions } from '../../modules/Indicators/Services/IndicatorsPermissions';
import { NewsPermissions } from '../../modules/News/Services/NewsPermissions';
import { OperationsPermissions } from '../../modules/Operations/Services/OperationsPermissions';
import { OffersPermissions } from '../../modules/Offers/Services/OffersPermissions';
import { QuizzesPermissions } from '../../modules/Quizzes/Services/QuizzesPermissions';
import { SectorsPermissions } from '../../modules/Sectors/Services/SectorsPermissions';
import { ShopsPermissions } from '../../modules/Shops/Services/ShopsPermissions';
import { StatsPermissions } from '../../modules/Stats/Services/StatsPermissions';
import { SurveysPermissions } from '../../modules/Surveys/Services/SurveysPermissions';
import { TeamsPermissions } from '../../modules/Teams/Services/TeamsPermissions';
import { VideosPermissions } from '../../modules/Videos/Services/VideosPermissions';
import { AdminPermission } from '../commons/Me';
import { HierarchyGroupsPermissions } from '../../modules/HierarchyGroups/Services/HierarchyGroupsPermissions';
import { GlobalPermissions } from './GlobalPermissions';
import { ForecastsPermissions } from '../../modules/Offers/Services/ForecastsPermissions';
import { PinCodesPermissions } from '../../modules/PinCodes/Services/PinCodesPermission';
import { EntitiesPermissions } from '../../modules/Entities/Services/EntitiesPermissions';

export class PermissionsActions {
    public static SET_INITIALIZED = 'PERMISSIONS_SET_INITIALIZED';

    public static setInitialized = (initialized: boolean) => ({
        type: PermissionsActions.SET_INITIALIZED,
        initialized,
    });

    public static shouldHideAdminSection = () =>
        !BackUsersPermissions.Admin.canRead &&
        !TeamsPermissions.Admin.canRead &&
        !AreasPermissions.Admin.canRead &&
        !SectorsPermissions.Admin.canRead &&
        !ShopsPermissions.Admin.canRead &&
        !FrontUsersPermissions.Admin.canRead &&
        !StatsPermissions.Admin.canRead;

    public static initPerms = (adminPerms: Array<AdminPermission>) => {
        return (dispatch: any, getState: any) => {
            EnvironmentHelper.isDev() &&
                console.log(
                    `%c ${'ADMIN PERMISSIONS ON MODULE'.padEnd(window.innerWidth)}`,
                    'background: #33AACC; color: #333'
                );

            GlobalPermissions.initializeGlobalPermissions(adminPerms);

            //* ADMINS MODULES
            BackUsersPermissions.initializeBackUsersAdminPermissions(adminPerms);
            TeamsPermissions.initializeTeamsAdminPermissions(adminPerms);
            EntitiesPermissions.initializeEntitiesAdminPermissions(adminPerms);
            AreasPermissions.initializeAreasAdminPermissions(adminPerms);
            SectorsPermissions.initializeSectorsAdminPermissions(adminPerms);
            HierarchyGroupsPermissions.initializeHierarchyGroupsAdminPermissions(adminPerms);
            ShopsPermissions.initializeShopsAdminPermissions(adminPerms);
            FrontUsersPermissions.initializeFrontUsersAdminPermissions(adminPerms);
            StatsPermissions.initializeStatsAdminPermissions(adminPerms);
            PinCodesPermissions.initializePinCodesAdminPermissions(adminPerms);

            //* CONTENTS MODULE
            CollectionsPermissions.initializeCollectionsAdminPermissions(adminPerms);
            OffersPermissions.initializeOffersAdminPermissions(adminPerms);
            ForecastsPermissions.initializeForecastsAdminPermissions(adminPerms);
            OperationsPermissions.initializeOperationsAndEngagementsAdminPermissions(adminPerms);
            IndicatorsPermissions.initializeIndicatorsAdminPermissions(adminPerms);
            ChallengesPermissions.initializeChallengesAdminPermissions(adminPerms);
            QuizzesPermissions.initializeQuizzesAdminPermissions(adminPerms);
            EventsPermissions.initializeEventsAndRemindersAdminPermissions(adminPerms);
            CommunicationsPermissions.initCommunicationsAdminPermissions(adminPerms);
            NewsPermissions.initializeNewsAdminPermissions(adminPerms);
            SurveysPermissions.initializeSurveysAdminPermissions(adminPerms);
            AuditsPermissions.initializeAuditsAdminPermissions(adminPerms);
            VideosPermissions.initializeVideosAdminPermissions(adminPerms);
            GondolesPermissions.initializeGondolesAdminPermissions(adminPerms);
            ContactsPermissions.initializeContactsAdminPermissions(adminPerms);

            logAdminPermissions(adminPerms);
            dispatch(PermissionsActions.setInitialized(true));
        };
    };
}

//#region //! LOG

const logAdminPermissions = (adminPerms: Array<AdminPermission>) => {
    if (EnvironmentHelper.isDev()) {
        const permissionsByModule = ListUtils.groupBy(adminPerms, (x) => x.module);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const displayPermissionsInTable = () => {
            const permissionsTable: Record<string, any> = {};
            permissionsByModule.keys.forEach((code, index) => {
                const perms = permissionsByModule.map.get(code);
                permissionsTable[code] = perms
                    ? { Permissions: perms.map((x) => x.permission).join(' | ') }
                    : {};
            });
			console.table(permissionsTable);
        };

        const displayPermissionsPerLines = () => {
            const permissionsNames = Array.from(new Set(adminPerms.map((x) => x.permission)));
            let longestPermissionLength = Math.max(...permissionsNames.map((x) => x.length));
			console.log(
				`%c ${'ADMIN PERMISSIONS'.padEnd(window.innerWidth)}`,
				'background: #33AACC; color: #333'
			);
            permissionsByModule.keys.forEach((code) => {
                const perms = permissionsByModule.map.get(code);
                const logNameFormat = (perm: AdminPermission) =>
                    `%c ${perm.permission.padEnd(longestPermissionLength)}`;

                const logColorFormat = (perm: AdminPermission): Array<string> => [
                    `background: transparent; color: ${perm.permission ? '#FFF' : '#FFF'}`,
                ];

                if (perms) {
                    const message = `%c${code.padStart(25)} %c  ${perms
						.map(logNameFormat)
						.join(' | ')}`;
                    let colorStyles: Array<string> = [
                        'background: #F4A59F; color: #190C0A',
                        'background: transparent; color: #FFFFFF',
                    ];
                    perms.forEach((item) => {
                        colorStyles = [...colorStyles, ...logColorFormat(item)];
                    });
					console.log(message, ...colorStyles);
                }
            });
        };

        displayPermissionsPerLines();
    }
};

//#endregion
